import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Tab,
  Nav,
  Spinner,
  Alert,
  Table,
  ProgressBar,
} from 'react-bootstrap';
import { toast } from '../common/Toast/toast';
import {
  Filter,
  Calendar,
  Users,
  TrendingUp,
  BarChart3,
  LineChart,
  Download,
  AlertCircle,
  RefreshCw,
  Eye,
  Activity,
  Target,
  Award,
  BookOpen,
  GraduationCap,
  CheckCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

// Import your API services
import {
  getAttendanceReport,
  getAttendanceReportBatch,
  getAttendanceReportOptions,
} from '../../../services/MarkattendancesService';
import BatchDetailsModal from './BatchDetailsModal.jsx';
import ExportDialog from '../common/Export/ExportDialog';
import { styles } from './style.jsx';
// Inline styles for the dashboard

// StatBox component for trainer cards
const StatBox = ({ value, label, color }) => (
  <div
    style={{
      background: 'white',
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      border: '1px solid #e2e8f0',
    }}
  >
    <h5 className={`mb-1 ${color}`} style={{ fontWeight: '700' }}>
      {value}
    </h5>
    <small className="text-muted">{label}</small>
  </div>
);

// Batch Details Modal Component

const AttendanceAnalyticsDashboard = () => {
  // State management
  const [loading, setLoading] = useState({
    courses: false,
    batches: false,
    trainers: false,
    attendance: false,
    analytics: false,
  });
  const [filters, setFilters] = useState({
    courseId: '',
    batchId: '',
    trainerId: '',
    dateRange: { start: '', end: '' },
  });
  // console.log("filters",filtersfilters)
  // The filter dropdowns' options (GET /attendances/report/options).
  const [data, setData] = useState({
    courses: [],
    batches: [],
    trainers: [],
    filteredBatches: [],
    filteredTrainers: [],
  });
  // The report as the API sends it (GET /attendances/report).
  const EMPTY_ANALYTICS = {
    courseLevel: null,
    trainerLevel: null,
    allTrainersInCourse: [],
    batchLevel: [],
    summary: null,
    dailyAttendance: [],
    dailySeries: [],
    distribution: null,
  };
  const [analytics, setAnalytics] = useState(EMPTY_ANALYTICS);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // New state for batch details modal
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const COLORS = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#ec4899',
    '#14b8a6',
    '#f97316',
    '#6366f1',
    '#84cc16',
    '#a855f7',
    '#22c55e',
    '#eab308',
    '#e11d48',
    '#0ea5e9',
    '#d946ef',
    '#2dd4bf',
    '#fb923c',
    '#818cf8',
    '#4ade80',
    '#c084fc',
    '#34d399',
    '#fbbf24',
    '#f43f5e',
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  /* The filter lists. Every figure on this page now comes from the API
     (lms-api attendances/attendance.report.js), which reads all matching
     sessions. The page used to download the course, batch, trainer and
     attendance lists and compute the report itself -- but each request is
     capped at 100 rows, so the report described the newest 100 sessions of
     the first 100 batches. These lists are complete. */
  const fetchAllData = async () => {
    try {
      setLoading((prev) => ({ ...prev, courses: true, batches: true, trainers: true }));
      const options = await getAttendanceReportOptions();
      setData({
        courses: options.courses || [],
        batches: options.batches || [],
        trainers: options.trainers || [],
        filteredBatches: options.batches || [],
        filteredTrainers: options.trainers || [],
      });
    } catch (err) {
      setError('Failed to fetch initial data. Please try again.');
      console.error('Error fetching data:', err);
    } finally {
      setLoading((prev) => ({ ...prev, courses: false, batches: false, trainers: false }));
    }
  };

  // The date inputs already hold YYYY-MM-DD, which is what the API takes.
  const dateParams = () => ({
    from: filters.dateRange.start || undefined,
    to: filters.dateRange.end || undefined,
  });

  // The batch popup's figures, per day and per session, from the API.
  const handleViewBatchDetails = async (batchId) => {
    try {
      const detail = await getAttendanceReportBatch(batchId, dateParams());
      if (!detail) return;
      setSelectedBatch(detail);
      setShowBatchModal(true);
    } catch (err) {
      console.error('Error fetching batch details:', err);
      toast.error('Failed to load batch details. Please try again.');
    }
  };

  const handleFilterChange = (filterType, value) => {
    const updatedFilters = { ...filters, [filterType]: value };

    // Reset dependent filters when cleared
    if (value === '') {
      if (filterType === 'courseId') {
        updatedFilters.batchId = '';
        updatedFilters.trainerId = '';
      } else if (filterType === 'batchId') {
        updatedFilters.trainerId = '';
      }
    }

    /* Narrow the other dropdowns to what goes with the choice. The API
       sends each batch's trainerId as the trainer record's id, so these are
       plain comparisons against the complete lists. */
    let filteredBatches = data.batches;
    if (updatedFilters.courseId) {
      filteredBatches = filteredBatches.filter((batch) => batch.courseId === updatedFilters.courseId);
    }
    if (updatedFilters.batchId) {
      filteredBatches = filteredBatches.filter((batch) => batch.id === updatedFilters.batchId);
    }

    let filteredTrainers = data.trainers;
    if (updatedFilters.trainerId) {
      filteredTrainers = data.trainers.filter((trainer) => trainer.id === updatedFilters.trainerId);
    } else if (updatedFilters.batchId || updatedFilters.courseId) {
      const trainerIds = new Set(filteredBatches.map((batch) => batch.trainerId).filter(Boolean));
      filteredTrainers = data.trainers.filter((trainer) => trainerIds.has(trainer.id));
    }

    setData((prev) => ({
      ...prev,
      filteredBatches,
      filteredTrainers,
    }));

    setFilters(updatedFilters);
  };

  const handleDateChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: value,
      },
    }));
  };

  // The report, whole, from the API. Nothing is worked out on this page.
  const calculateAnalytics = useCallback(async () => {
    if (!filters.courseId && !filters.batchId && !filters.trainerId) {
      setError('Please select at least one filter to analyze');
      return;
    }

    setLoading((prev) => ({ ...prev, analytics: true }));
    setError(null);

    try {
      const report = await getAttendanceReport({
        courseId: filters.courseId || undefined,
        batchId: filters.batchId || undefined,
        trainerId: filters.trainerId || undefined,
        from: filters.dateRange.start || undefined,
        to: filters.dateRange.end || undefined,
      });
      if (!report) throw new Error('The report came back empty');
      setAnalytics({
        courseLevel: report.courseLevel || null,
        trainerLevel: report.trainerLevel || null,
        allTrainersInCourse: report.allTrainersInCourse || [],
        batchLevel: report.batchLevel || [],
        summary: report.summary || null,
        dailyAttendance: report.dailyAttendance || [],
        dailySeries: report.dailySeries || [],
        distribution: report.distribution || null,
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to calculate analytics. Please try again.');
      console.error('Error calculating analytics:', err);
    } finally {
      setLoading((prev) => ({ ...prev, analytics: false }));
    }
  }, [filters]);

  // One chart series per batch in the report: { key, name }.
  const getBatchesForDailyTrend = () => analytics.dailySeries || [];

  const getBatchComparisonData = () =>
    analytics.batchLevel.map((batch, index) => ({
      name: `B${index + 1}`,
      fullName: batch.batchName,
      attendance: batch.attendancePercentage || 0,
      students: batch.totalStudents || 0,
      lectures: batch.totalLectures || 0,
      color: COLORS[index % COLORS.length],
      batchId: batch.batchId,
      present: batch.totalPresent || 0,
      possible: batch.totalPossible || 0,
    }));

  // The API counts the batches per band; this only names and colours them.
  const getAttendanceDistribution = () => {
    const d = analytics.distribution || {};
    return [
      { name: 'Excellent (90%+)', value: d.excellent || 0, color: '#10b981' },
      { name: 'Good (75-89%)', value: d.good || 0, color: '#3b82f6' },
      { name: 'Average (60-74%)', value: d.average || 0, color: '#f59e0b' },
      { name: 'Poor (<60%)', value: d.poor || 0, color: '#ef4444' },
    ];
  };

  const handleAnalyze = (e) => {
    e.preventDefault();
    calculateAnalytics();
  };

  const handleReset = () => {
    setFilters({
      courseId: '',
      batchId: '',
      trainerId: '',
      dateRange: { start: '', end: '' },
    });
    setData((prev) => ({
      ...prev,
      filteredBatches: data.batches,
      filteredTrainers: data.trainers,
    }));
    setAnalytics(EMPTY_ANALYTICS);
    setError(null);
  };

  /*
   * The batch table, for the shared export popup.
   *
   * This button used to open a menu of PDF / Excel / CSV, and every one
   * of the three logged to the console and raised a SweetAlert saying
   * "Exporting data as ..." -- a page that told the reader it had done
   * something it had not done at all. It is one button now, and the
   * dialog behind it writes the file the popup names.
   */
  const exportColumns = [
    { key: 'batchName', label: 'Batch', value: (b) => b.batchName },
    { key: 'courseName', label: 'Course', value: (b) => b.courseName },
    { key: 'trainerName', label: 'Trainer', value: (b) => b.trainerName },
    { key: 'mode', label: 'Mode', value: (b) => b.mode },
    { key: 'days', label: 'Days', value: (b) => b.days },
    { key: 'mainSubject', label: 'Subject', value: (b) => b.mainSubject },
    { key: 'totalStudents', label: 'Students', value: (b) => b.totalStudents },
    { key: 'totalLectures', label: 'Lectures', value: (b) => b.totalLectures },
    { key: 'totalPresent', label: 'Present marks', value: (b) => b.totalPresent },
    {
      key: 'totalPossible',
      label: 'Possible marks',
      value: (b) => b.totalPossible,
    },
    {
      key: 'attendancePercentage',
      label: 'Attendance %',
      value: (b) => b.attendancePercentage,
    },
  ];

  const exportPreamble = analytics.summary
    ? [
        'Attendance analytics',
        `Generated: ${new Date().toLocaleString()}`,
        `Courses: ${analytics.summary.totalCourses} | Batches: ${analytics.summary.totalBatches} | Trainers: ${analytics.summary.totalTrainers} | Students: ${analytics.summary.totalStudents}`,
        `Lectures: ${analytics.summary.totalLectures} | Overall attendance: ${analytics.summary.overallAttendance}%`,
      ]
    : [];

  const getStatusBadge = (percentage) => {
    if (percentage >= 90) return { text: 'Excellent', style: styles.badgeExcellent };
    if (percentage >= 75) return { text: 'Good', style: styles.badgeGood };
    if (percentage >= 60) return { text: 'Average', style: styles.badgeAverage };
    return { text: 'Needs Attention', style: styles.badgePoor };
  };

  const getProgressVariant = (percentage) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 75) return 'primary';
    if (percentage >= 60) return 'warning';
    return 'danger';
  };

  // Loading State
  if (loading.courses || loading.batches || loading.trainers) {
    return (
      <div style={styles.loadingContainer}>
        <Spinner
          animation="border"
          variant="primary"
          style={{ width: '60px', height: '60px', borderWidth: '4px' }}
          className="mb-3"
        />
        <h5 className="text-muted">Loading Dashboard Data...</h5>
        <p className="text-muted small">Please wait while we fetch the latest information</p>
      </div>
    );
  }

  return (
    <div style={styles.dashboardContainer}>
      <Container fluid className="px-4 py-3">
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            <div style={styles.dashboardHeader}>
              <Row className="align-items-center">
                <Col>
                  <h2 className="mb-1 d-flex align-items-center gap-3">
                    <BarChart3 size={32} />
                    Attendance Analytics Dashboard
                  </h2>
                  <p className="mb-0 opacity-75">
                    Comprehensive insights into attendance patterns and performance metrics
                  </p>
                </Col>
                <Col xs="auto">
                  <div className="d-flex gap-2 align-items-center">
                    <Button
                      variant="light"
                      style={styles.btnOutlineEnhanced}
                      onClick={fetchAllData}
                      disabled={loading.attendance}
                      className="d-flex align-items-center gap-2"
                    >
                      <RefreshCw size={18} />
                      <span className="d-none d-md-inline">Refresh</span>
                    </Button>
                    {/* One button, not a menu of three formats: the
                        dialog is where the format is chosen now, and it
                        offers the two this app can actually write. */}
                    <Button
                      variant="light"
                      style={styles.btnOutlineEnhanced}
                      onClick={() => setExportOpen(true)}
                      disabled={analytics.batchLevel.length === 0}
                      aria-haspopup="dialog"
                      aria-expanded={exportOpen}
                      className="d-flex align-items-center gap-2"
                    >
                      <Download size={18} />
                      <span className="d-none d-md-inline">Export</span>
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Error Alert */}
        {error && (
          <Row className="mb-4">
            <Col>
              <Alert
                variant="danger"
                onClose={() => setError(null)}
                dismissible
                className="d-flex align-items-center gap-2"
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                  color: '#dc2626',
                }}
              >
                <AlertCircle size={20} />
                <span>{error}</span>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Filter Card */}
        <Card style={styles.filterCard} className="mb-4 shadow-sm">
          <Card.Header className="bg-white py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Filter size={20} className="text-primary" />
                <strong className="mb-0">Filter & Analyze</strong>
              </div>
              {analytics.summary && (
                <Badge
                  bg="success"
                  style={styles.badgeEnhanced}
                  className="d-flex align-items-center gap-1"
                >
                  <CheckCircle size={14} />
                  Filters Active
                </Badge>
              )}
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <Form onSubmit={handleAnalyze}>
              <Row className="g-3 mb-3">
                <Col md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small text-uppercase d-flex align-items-center gap-2">
                      <GraduationCap size={16} />
                      Course
                    </Form.Label>
                    <Form.Select
                      style={styles.formControlEnhanced}
                      value={filters.courseId}
                      onChange={(e) => handleFilterChange('courseId', e.target.value)}
                    >
                      <option value="">All Courses</option>
                      {data.courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small text-uppercase d-flex align-items-center gap-2">
                      <BookOpen size={16} />
                      Batch
                    </Form.Label>
                    <Form.Select
                      style={styles.formControlEnhanced}
                      value={filters.batchId}
                      onChange={(e) => handleFilterChange('batchId', e.target.value)}
                    >
                      <option value="">All Batches</option>
                      {data.filteredBatches.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {batch.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6} lg={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small text-uppercase d-flex align-items-center gap-2">
                      <Users size={16} />
                      Trainer
                    </Form.Label>
                    <Form.Select
                      style={styles.formControlEnhanced}
                      value={filters.trainerId}
                      onChange={(e) => handleFilterChange('trainerId', e.target.value)}
                    >
                      <option value="">All Trainers</option>
                      {data.filteredTrainers.map((trainer) => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6} lg={3}>
                  <Form.Label className="fw-semibold text-muted small text-uppercase d-flex align-items-center gap-2">
                    <Calendar size={16} />
                    Date Range
                  </Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="date"
                      style={styles.formControlEnhanced}
                      value={filters.dateRange.start}
                      onChange={(e) => handleDateChange('start', e.target.value)}
                    />
                    <Form.Control
                      type="date"
                      style={styles.formControlEnhanced}
                      value={filters.dateRange.end}
                      onChange={(e) => handleDateChange('end', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                <div className="text-muted small">
                  {filters.courseId || filters.batchId || filters.trainerId ? (
                    <span className="d-flex align-items-center gap-2">
                      <Activity size={16} className="text-primary" />
                      {(() => {
                        const activeFilters = [];
                        if (filters.courseId) activeFilters.push('Course');
                        if (filters.batchId) activeFilters.push('Batch');
                        if (filters.trainerId) activeFilters.push('Trainer');
                        if (filters.dateRange.start || filters.dateRange.end)
                          activeFilters.push('Date Range');
                        return `Active filters: ${activeFilters.join(', ')}`;
                      })()}
                    </span>
                  ) : (
                    <span>Select at least one filter to begin analysis</span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={handleReset}
                    style={styles.btnOutlineEnhanced}
                    className="d-flex align-items-center gap-2"
                  >
                    <RefreshCw size={18} />
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    style={styles.btnPrimaryEnhanced}
                    disabled={
                      loading.analytics ||
                      (!filters.courseId && !filters.batchId && !filters.trainerId)
                    }
                    className="d-flex align-items-center gap-2 text-white"
                  >
                    {loading.analytics ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <TrendingUp size={18} />
                        Analyze Data
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>
        {/* style,  */}
        {/* Active Filters Summary */}
        {analytics.summary && (
          <div style={styles.filterSummaryCard} className="mb-4">
            <Row className="align-items-center">
              <Col>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <span className="fw-semibold text-primary me-2 d-flex align-items-center gap-2">
                    <Filter size={16} />
                    Active Filters:
                  </span>
                  {filters.courseId && (
                    <Badge
                      bg="primary"
                      style={styles.badgeEnhanced}
                      className="d-flex align-items-center gap-1"
                    >
                      <GraduationCap size={12} />
                      {analytics.courseLevel?.courseName ||
                        data.courses.find((c) => c.id === filters.courseId)?.name}
                    </Badge>
                  )}
                  {filters.batchId && (
                    <Badge
                      bg="success"
                      style={styles.badgeEnhanced}
                      className="d-flex align-items-center gap-1"
                    >
                      <BookOpen size={12} />
                      {data.batches.find((b) => b.id === filters.batchId)?.name}
                    </Badge>
                  )}
                  {filters.trainerId && (
                    <Badge
                      bg="warning"
                      text="dark"
                      style={styles.badgeEnhanced}
                      className="d-flex align-items-center gap-1"
                    >
                      <Users size={12} />
                      {data.trainers.find((t) => t.id === filters.trainerId)?.name}
                    </Badge>
                  )}
                  {(filters.dateRange.start || filters.dateRange.end) && (
                    <Badge
                      bg="info"
                      style={styles.badgeEnhanced}
                      className="d-flex align-items-center gap-1"
                    >
                      <Calendar size={12} />
                      {filters.dateRange.start || 'Start'} → {filters.dateRange.end || 'End'}
                    </Badge>
                  )}
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <Badge bg="secondary" style={styles.badgeEnhanced}>
                      {analytics.summary?.totalBatches || 0} Batches
                    </Badge>
                    <Badge bg="secondary" style={styles.badgeEnhanced}>
                      {analytics.summary?.totalStudents || 0} Students
                    </Badge>
                    {analytics.allTrainersInCourse.length > 0 && (
                      <Badge bg="secondary" style={styles.badgeEnhanced}>
                        {analytics.allTrainersInCourse.length} Trainers
                      </Badge>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Analytics Results */}
        {analytics.summary ? (
          <>
            {/* Summary Stats Cards */}
            <Row className="mb-4 g-3">
              <Col xs={6} lg={3}>
                <Card
                  style={{ ...styles.statCard, ...styles.statCardPrimary }}
                  className="h-100 shadow-sm"
                >
                  <Card.Body className="text-center p-4">
                    <div style={{ ...styles.statIcon, ...styles.statIconPrimary }}>
                      <Users size={28} color="#3b82f6" />
                    </div>
                    <div style={styles.statValue}>{analytics.summary.totalStudents}</div>
                    <div style={styles.statLabel}>Total Students</div>
                    <small className="text-muted mt-2 d-block">Enrolled across batches</small>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={6} lg={3}>
                <Card
                  style={{ ...styles.statCard, ...styles.statCardSuccess }}
                  className="h-100 shadow-sm"
                >
                  <Card.Body className="text-center p-4">
                    <div style={{ ...styles.statIcon, ...styles.statIconSuccess }}>
                      <Target size={28} color="#10b981" />
                    </div>
                    <div style={styles.statValue}>{analytics.summary.overallAttendance}%</div>
                    <div style={styles.statLabel}>Overall Attendance</div>
                    <ProgressBar
                      now={analytics.summary.overallAttendance}
                      variant={getProgressVariant(analytics.summary.overallAttendance)}
                      className="mt-3"
                      style={{ height: '10px', borderRadius: '10px' }}
                    />
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={6} lg={3}>
                <Card
                  style={{ ...styles.statCard, ...styles.statCardInfo }}
                  className="h-100 shadow-sm"
                >
                  <Card.Body className="text-center p-4">
                    <div style={{ ...styles.statIcon, ...styles.statIconInfo }}>
                      <BookOpen size={28} color="#06b6d4" />
                    </div>
                    <div style={styles.statValue}>{analytics.summary.totalBatches}</div>
                    <div style={styles.statLabel}>Total Batches</div>
                    <small className="text-muted mt-2 d-block">
                      {filters.courseId ? 'In selected course' : 'Analyzed'}
                    </small>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={6} lg={3}>
                <Card
                  style={{ ...styles.statCard, ...styles.statCardWarning }}
                  className="h-100 shadow-sm"
                >
                  <Card.Body className="text-center p-4">
                    <div style={{ ...styles.statIcon, ...styles.statIconWarning }}>
                      <Award size={28} color="#f59e0b" />
                    </div>
                    <div style={styles.statValue}>{analytics.summary.totalTrainers}</div>
                    <div style={styles.statLabel}>Total Trainers</div>
                    <small className="text-muted mt-2 d-block">Assigned to batches</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Tabs Section */}
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <Card style={styles.analyticsCard} className="mb-4 shadow-sm">
                <Card.Header
                  className="bg-white py-0"
                  style={{ borderBottom: '2px solid #e2e8f0' }}
                >
                  <Nav variant="tabs" className="border-0">
                    <Nav.Item>
                      <Nav.Link
                        eventKey="overview"
                        className="d-flex align-items-center gap-2 py-3 px-4"
                        style={{
                          border: 'none',
                          borderBottom:
                            activeTab === 'overview'
                              ? '2px solid #3b82f6'
                              : '2px solid transparent',
                          marginBottom: '-2px',
                        }}
                      >
                        <BarChart3 size={18} />
                        Overview
                        {analytics.batchLevel.length > 0 && (
                          <Badge bg="primary" pill className="ms-1">
                            {analytics.batchLevel.length}
                          </Badge>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="daily"
                        className="d-flex align-items-center gap-2 py-3 px-4"
                        style={{
                          border: 'none',
                          borderBottom:
                            activeTab === 'daily' ? '2px solid #3b82f6' : '2px solid transparent',
                          marginBottom: '-2px',
                        }}
                      >
                        <LineChart size={18} />
                        Daily Trend
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="batches"
                        className="d-flex align-items-center gap-2 py-3 px-4"
                        style={{
                          border: 'none',
                          borderBottom:
                            activeTab === 'batches' ? '2px solid #3b82f6' : '2px solid transparent',
                          marginBottom: '-2px',
                        }}
                      >
                        <Users size={18} />
                        Batch Details
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="trainer"
                        className="d-flex align-items-center gap-2 py-3 px-4"
                        style={{
                          border: 'none',
                          borderBottom:
                            activeTab === 'trainer' ? '2px solid #3b82f6' : '2px solid transparent',
                          marginBottom: '-2px',
                        }}
                      >
                        <Eye size={18} />
                        Trainer Analytics
                        {filters.courseId && analytics.allTrainersInCourse.length > 0 && (
                          <Badge bg="primary" pill className="ms-1">
                            {analytics.allTrainersInCourse.length}
                          </Badge>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>
                <Card.Body className="p-4">
                  <Tab.Content>
                    {/* Overview Tab */}
                    <Tab.Pane eventKey="overview">
                      <Row className="g-4">
                        <Col lg={8}>
                          <Card style={styles.analyticsCard} className="h-100 shadow-sm">
                            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                              <strong>Batch-wise Attendance Comparison</strong>
                              <Badge bg="secondary" style={styles.badgeEnhanced}>
                                {getBatchComparisonData().length} batches
                              </Badge>
                            </Card.Header>
                            <Card.Body style={{ height: '400px', padding: '1rem' }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={getBatchComparisonData()}
                                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                  <XAxis
                                    dataKey="name"
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    interval={0}
                                    tick={{ fontSize: 12, fill: '#64748b' }}
                                  />
                                  <YAxis
                                    domain={[0, 100]}
                                    tick={{ fill: '#64748b' }}
                                    label={{
                                      value: 'Attendance %',
                                      angle: -90,
                                      position: 'insideLeft',
                                      fill: '#64748b',
                                    }}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      borderRadius: '12px',
                                      border: 'none',
                                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                    }}
                                    content={({ active, payload }) => {
                                      if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                          <div
                                            style={{
                                              background: 'white',
                                              padding: '12px 16px',
                                              borderRadius: '12px',
                                              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                            }}
                                          >
                                            <p
                                              className="fw-bold mb-1"
                                              style={{ color: data.color }}
                                            >
                                              {data.fullName}
                                            </p>
                                            <p className="mb-0 small">
                                              Attendance: <strong>{data.attendance}%</strong>
                                            </p>
                                            <p className="mb-0 small">Students: {data.students}</p>
                                            <p className="mb-0 small">Lectures: {data.lectures}</p>
                                          </div>
                                        );
                                      }
                                      return null;
                                    }}
                                  />
                                  <Bar dataKey="attendance" radius={[6, 6, 0, 0]}>
                                    {getBatchComparisonData().map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col lg={4}>
                          <Card style={styles.analyticsCard} className="h-100 shadow-sm">
                            <Card.Header className="bg-white">
                              <strong>Attendance Distribution</strong>
                            </Card.Header>
                            <Card.Body style={{ height: '400px', padding: '1rem' }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <RechartsPieChart>
                                  <Pie
                                    data={getAttendanceDistribution()}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                  >
                                    {getAttendanceDistribution().map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={{
                                      borderRadius: '12px',
                                      border: 'none',
                                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                    }}
                                  />
                                  <Legend verticalAlign="bottom" height={36} />
                                </RechartsPieChart>
                              </ResponsiveContainer>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Tab.Pane>

                    {/* Daily Trend Tab */}
                    <Tab.Pane eventKey="daily">
                      <Card style={styles.analyticsCard} className="shadow-sm">
                        <Card.Header className="bg-white">
                          <strong>Daily Attendance Trend</strong>
                          <div className="small text-muted mt-1">
                            Showing {getBatchesForDailyTrend().length} batch
                            {getBatchesForDailyTrend().length !== 1 ? 'es' : ''}
                          </div>
                        </Card.Header>
                        <Card.Body style={{ height: '450px', padding: '1rem' }}>
                          {analytics.dailyAttendance.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart
                                data={analytics.dailyAttendance}
                                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                              >
                                <defs>
                                  {getBatchesForDailyTrend().map((series, index) => {
                                    const batchKey = series.key;
                                    return (
                                      <linearGradient
                                        key={batchKey}
                                        id={`gradient-${batchKey}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="5%"
                                          stopColor={COLORS[index % COLORS.length]}
                                          stopOpacity={0.3}
                                        />
                                        <stop
                                          offset="95%"
                                          stopColor={COLORS[index % COLORS.length]}
                                          stopOpacity={0}
                                        />
                                      </linearGradient>
                                    );
                                  })}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                  dataKey="date"
                                  angle={-45}
                                  textAnchor="end"
                                  height={60}
                                  tick={{ fontSize: 10, fill: '#64748b' }}
                                />
                                <YAxis
                                  domain={[0, 100]}
                                  tick={{ fill: '#64748b' }}
                                  label={{
                                    value: 'Attendance %',
                                    angle: -90,
                                    position: 'insideLeft',
                                    fill: '#64748b',
                                  }}
                                />
                                <Tooltip
                                  contentStyle={{
                                    borderRadius: '12px',
                                    border: 'none',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                  }}
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      return (
                                        <div
                                          style={{
                                            background: 'white',
                                            padding: '12px 16px',
                                            borderRadius: '12px',
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                          }}
                                        >
                                          <p className="fw-bold mb-2">{label}</p>
                                          {payload.map((entry, index) => {
                                            const batchKey = entry.dataKey.replace(
                                              '_attendance',
                                              ''
                                            );
                                            const batchName =
                                              analytics.dailyAttendance[0]?.[`${batchKey}_name`] ||
                                              `Batch ${index + 1}`;
                                            return (
                                              <p key={index} className="mb-1 small">
                                                <span
                                                  style={{ color: entry.color, fontWeight: 'bold' }}
                                                >
                                                  {batchName}:
                                                </span>{' '}
                                                {entry.value}%
                                              </p>
                                            );
                                          })}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                {/* Each Area is named after its batch, so the legend reads right as-is. */}
                                <Legend verticalAlign="bottom" height={36} />
                                {getBatchesForDailyTrend().map((series, index) => {
                                  const batchKey = series.key;
                                  return (
                                    <Area
                                      key={batchKey}
                                      type="monotone"
                                      dataKey={batchKey}
                                      name={series.name || `Batch ${index + 1}`}
                                      stroke={COLORS[index % COLORS.length]}
                                      fill={`url(#gradient-${batchKey})`}
                                      strokeWidth={2}
                                    />
                                  );
                                })}
                              </AreaChart>
                            </ResponsiveContainer>
                          ) : (
                            <div style={styles.emptyState}>
                              <div style={styles.emptyStateIcon}>
                                <LineChart size={32} className="text-muted" />
                              </div>
                              <h5 className="text-muted">No Daily Data Available</h5>
                              <p className="text-muted small">
                                Daily attendance data will appear here once available
                              </p>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Batch Details Tab */}
                    <Tab.Pane eventKey="batches">
                      <Card style={styles.analyticsCard} className="shadow-sm">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                          <strong>Batch Details</strong>
                          <Badge bg="primary" style={styles.badgeEnhanced}>
                            {analytics.batchLevel.length} Batches
                          </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                          <div className="table-responsive">
                            <Table hover className="mb-0" style={styles.tableEnhanced}>
                              <thead>
                                <tr
                                  style={{
                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                  }}
                                >
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Batch Name
                                  </th>
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Trainer
                                  </th>
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted text-center"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Students
                                  </th>
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted text-center"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Lectures
                                  </th>
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Attendance
                                  </th>
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted text-center"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Status
                                  </th>
                                  <th
                                    className="py-3 px-4 text-uppercase small fw-bold text-muted text-center"
                                    style={{ letterSpacing: '0.5px' }}
                                  >
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {analytics.batchLevel.map((batch, index) => {
                                  const status = getStatusBadge(batch.attendancePercentage);
                                  return (
                                    <tr key={batch.batchId || batch.id}>
                                      <td className="py-3 px-4">
                                        <div className="d-flex align-items-center gap-2">
                                          <div
                                            style={{
                                              width: '8px',
                                              height: '8px',
                                              borderRadius: '50%',
                                              background: COLORS[index % COLORS.length],
                                            }}
                                          />
                                          <span className="fw-semibold">{batch.batchName}</span>
                                        </div>
                                      </td>
                                      <td className="py-3 px-4">{batch.trainerName}</td>
                                      <td className="py-3 px-4 text-center">
                                        {batch.totalStudents}
                                      </td>
                                      <td className="py-3 px-4 text-center">
                                        {batch.totalLectures}
                                      </td>
                                      <td className="py-3 px-4" style={{ minWidth: '180px' }}>
                                        <div className="d-flex align-items-center gap-2">
                                          <ProgressBar
                                            now={batch.attendancePercentage}
                                            variant={getProgressVariant(batch.attendancePercentage)}
                                            style={{ height: '8px', borderRadius: '8px', flex: 1 }}
                                          />
                                          <span className="fw-bold" style={{ minWidth: '45px' }}>
                                            {batch.attendancePercentage}%
                                          </span>
                                        </div>
                                      </td>
                                      <td className="py-3 px-4 text-center">
                                        <span style={{ ...styles.badgeEnhanced, ...status.style }}>
                                          {status.text}
                                        </span>
                                      </td>
                                      <td className="py-3 px-4 text-center">
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() =>
                                            handleViewBatchDetails(batch.batchId || batch.id)
                                          }
                                          className="d-flex align-items-center gap-1 mx-auto"
                                          style={{ borderRadius: '8px' }}
                                        >
                                          <Eye size={14} />
                                          View Details
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>

                    {/* Trainer Analytics Tab */}
                    <Tab.Pane eventKey="trainer">
                      {/* Show trainer analytics based on selection */}
                      {filters.trainerId && analytics.trainerLevel ? (
                        <Row className="g-4">
                          <Col lg={4}>
                            <Card style={styles.trainerCard} className="h-100 shadow-sm">
                              <Card.Body className="p-4">
                                <div className="text-center mb-4">
                                  <div
                                    style={{
                                      width: '80px',
                                      height: '80px',
                                      borderRadius: '50%',
                                      background:
                                        'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto 1rem',
                                      color: 'white',
                                      fontSize: '2rem',
                                      fontWeight: '700',
                                    }}
                                  >
                                    {analytics.trainerLevel.trainerName?.charAt(0) || 'T'}
                                  </div>
                                  <h4 className="mb-1">{analytics.trainerLevel.trainerName}</h4>
                                  <p className="text-muted small mb-0">
                                    {analytics.trainerLevel.email}
                                  </p>

                                  {/* Show filter context */}
                                  {filters.batchId && (
                                    <Alert variant="info" className="mt-2 p-2 small">
                                      <strong>Showing data for selected batch only</strong>
                                    </Alert>
                                  )}

                                  <Badge bg="info" className="mt-2">
                                    {analytics.trainerLevel.specialization || 'General'}
                                  </Badge>
                                </div>

                                <Row className="g-3">
                                  <Col xs={6}>
                                    <div
                                      style={{
                                        background: 'white',
                                        borderRadius: '10px',
                                        padding: '1rem',
                                        textAlign: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                      }}
                                    >
                                      <h4 className="mb-0 text-primary">
                                        {analytics.trainerLevel.totalBatches}
                                      </h4>
                                      <small className="text-muted">
                                        {filters.batchId ? 'Selected Batch' : 'Total Batches'}
                                      </small>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div
                                      style={{
                                        background: 'white',
                                        borderRadius: '10px',
                                        padding: '1rem',
                                        textAlign: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                      }}
                                    >
                                      <h4 className="mb-0 text-success">
                                        {analytics.trainerLevel.totalStudents}
                                      </h4>
                                      <small className="text-muted">Total Students</small>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div
                                      style={{
                                        background: 'white',
                                        borderRadius: '10px',
                                        padding: '1rem',
                                        textAlign: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                      }}
                                    >
                                      <h4 className="mb-0 text-info">
                                        {analytics.trainerLevel.totalLectures}
                                      </h4>
                                      <small className="text-muted">Total Lectures</small>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div
                                      style={{
                                        background: 'white',
                                        borderRadius: '10px',
                                        padding: '1rem',
                                        textAlign: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                      }}
                                    >
                                      <h4 className="mb-0 text-warning">
                                        {analytics.trainerLevel.averageAttendance}%
                                      </h4>
                                      <small className="text-muted">Avg Attendance</small>
                                    </div>
                                  </Col>
                                </Row>

                                {/* Show batch details if available */}
                                {analytics.trainerLevel.batchesData &&
                                  analytics.trainerLevel.batchesData.length > 0 && (
                                    <div className="mt-4">
                                      <h6 className="mb-2">Batch Details:</h6>
                                      {analytics.trainerLevel.batchesData.map((batch, index) => (
                                        <div
                                          key={batch.batchId}
                                          className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                                        >
                                          <span className="fw-semibold">{batch.batchName}</span>
                                          <Badge
                                            bg={
                                              batch.attendancePercentage >= 75
                                                ? 'success'
                                                : batch.attendancePercentage >= 60
                                                  ? 'warning'
                                                  : 'danger'
                                            }
                                          >
                                            {batch.attendancePercentage}%
                                          </Badge>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col lg={8}>
                            <Card style={styles.analyticsCard} className="h-100 shadow-sm">
                              <Card.Header className="bg-white">
                                <strong>Trainer Performance Overview</strong>
                              </Card.Header>
                              <Card.Body style={{ height: '350px', padding: '1rem' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={[
                                      {
                                        name: 'Batches',
                                        value: analytics.trainerLevel.totalBatches,
                                        color: '#3b82f6',
                                      },
                                      {
                                        name: 'Students',
                                        value: analytics.trainerLevel.totalStudents,
                                        color: '#10b981',
                                      },
                                      {
                                        name: 'Lectures',
                                        value: analytics.trainerLevel.totalLectures,
                                        color: '#06b6d4',
                                      },
                                      {
                                        name: 'Attendance %',
                                        value: analytics.trainerLevel.averageAttendance,
                                        color: '#f59e0b',
                                      },
                                    ]}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fill: '#64748b' }} />
                                    <YAxis tick={{ fill: '#64748b' }} />
                                    <Tooltip
                                      contentStyle={{
                                        borderRadius: '12px',
                                        border: 'none',
                                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                                      }}
                                    />
                                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                      {[
                                        { color: '#3b82f6' },
                                        { color: '#10b981' },
                                        { color: '#06b6d4' },
                                        { color: '#f59e0b' },
                                      ].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      ) : filters.courseId && analytics.allTrainersInCourse?.length > 0 ? (
                        <div>
                          <h5 className="mb-3">
                            All Trainers in {analytics.courseLevel?.courseName}
                          </h5>

                          <Row className="g-4">
                            {analytics.allTrainersInCourse.map((trainer) => (
                              <Col key={trainer.trainerId || trainer.userId} lg={6} xl={4}>
                                <Card className="h-100 shadow-sm">
                                  <Card.Body className="p-3">
                                    {/* Trainer Header */}
                                    <div className="text-center mb-3">
                                      <div
                                        style={{
                                          width: 60,
                                          height: 60,
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg,#f59e0b,#d97706)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          margin: '0 auto 12px',
                                          color: '#fff',
                                          fontSize: '1.5rem',
                                          fontWeight: 700,
                                        }}
                                      >
                                        {trainer.trainerName?.charAt(0) || 'T'}
                                      </div>

                                      <h5 className="mb-1">{trainer.trainerName}</h5>
                                      <p className="text-muted small mb-2">{trainer.email}</p>

                                      <Badge
                                        bg={
                                          trainer.averageAttendance >= 75
                                            ? 'success'
                                            : trainer.averageAttendance >= 60
                                              ? 'warning'
                                              : 'danger'
                                        }
                                      >
                                        {trainer.averageAttendance}% Avg Attendance
                                      </Badge>
                                    </div>

                                    {/* Stats */}
                                    <Row className="g-2 mb-3">
                                      <Col xs={6}>
                                        <StatBox
                                          value={trainer.totalBatches}
                                          label="Batches"
                                          color="text-primary"
                                        />
                                      </Col>

                                      <Col xs={6}>
                                        <StatBox
                                          value={trainer.totalStudents}
                                          label="Students"
                                          color="text-success"
                                        />
                                      </Col>

                                      <Col xs={6}>
                                        <StatBox
                                          value={trainer.totalLectures}
                                          label="Lectures"
                                          color="text-info"
                                        />
                                      </Col>

                                      <Col xs={6}>
                                        <StatBox
                                          value={trainer.analyzedBatches}
                                          label="Active Batches"
                                          color="text-warning"
                                        />
                                      </Col>
                                    </Row>

                                    {/* Batches */}
                                    <div>
                                      <small className="text-muted d-block mb-1">
                                        Batches taught:
                                      </small>

                                      <div className="d-flex flex-wrap gap-1">
                                        {trainer.batchesData?.map((batch) => (
                                          <Badge
                                            key={batch.batchId}
                                            bg="light"
                                            text="dark"
                                            className="mb-1"
                                          >
                                            {batch.batchName} ({batch.attendancePercentage}%)
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      ) : (
                        <div style={styles.emptyState}>
                          <div style={styles.emptyStateIcon}>
                            <Users size={32} className="text-muted" />
                          </div>
                          <h5 className="text-muted">No Trainer Data Available</h5>
                          <p className="text-muted small">
                            Select a course or trainer from filters to view analytics
                          </p>
                        </div>
                      )}
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </Card>
            </Tab.Container>
          </>
        ) : (
          /* Empty State when no analytics data */
          <Card style={styles.analyticsCard} className="shadow-sm">
            <Card.Body style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>
                <BarChart3 size={48} className="text-muted" />
              </div>
              <h4 className="mb-3">No Analytics Data Available</h4>
              <p className="text-muted mb-4">
                Select filters and click "Analyze Data" to generate comprehensive attendance
                analytics and insights
              </p>
              <Button
                style={styles.btnPrimaryEnhanced}
                className="d-flex align-items-center gap-2 mx-auto text-white"
                onClick={() => {
                  if (data.courses.length > 0) {
                    handleFilterChange('courseId', data.courses[0].id);
                  }
                }}
                disabled={data.courses.length === 0}
              >
                <TrendingUp size={18} />
                Quick Start with First Course
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Loading Indicator */}
        {(loading.attendance || loading.analytics) && (
          <Alert
            variant="info"
            className="mt-4 d-flex align-items-center gap-2"
            style={{
              borderRadius: '12px',
              border: 'none',
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              color: '#0369a1',
            }}
          >
            <Spinner animation="border" size="sm" />
            {loading.attendance ? 'Loading attendance data...' : 'Calculating analytics...'}
          </Alert>
        )}
      </Container>

      {/* Batch Details Modal */}
      <BatchDetailsModal
        show={showBatchModal}
        onHide={() => setShowBatchModal(false)}
        detail={selectedBatch}
      />

      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Attendance analytics"
        columns={exportColumns}
        rows={analytics.batchLevel}
        filenameBase="attendance-analytics"
        sheetName="Batches"
        preamble={exportPreamble}
        scopeNote="One row per batch, under the filters set above."
      />
    </div>
  );
};

export default AttendanceAnalyticsDashboard;
